/** Three pulses, so a lost life stays readable after the blast fades. */
export const HIT_FLASHES = 3;
export const HIT_PULSE_MS = 240;
export const HIT_GAP_MS = 130;
const IMPACT_MS = 180;

function flashSpan(flashes: number): number {
  return flashes * HIT_PULSE_MS + Math.max(0, flashes - 1) * HIT_GAP_MS;
}

/** Time into the current pulse, or -1 during a gap and after the sequence. */
export function flashLocal(elapsedMs: number, flashes = HIT_FLASHES): number {
  if (elapsedMs < 0 || flashes < 1) return -1;
  const period = HIT_PULSE_MS + HIT_GAP_MS;
  const index = Math.floor(elapsedMs / period);
  if (index >= flashes) return -1;
  const local = elapsedMs - index * period;
  if (local >= HIT_PULSE_MS) return -1;
  return local;
}

export function hitActive(elapsedMs: number, flashes = HIT_FLASHES): boolean {
  return elapsedMs >= 0 && elapsedMs < flashSpan(flashes);
}

/** Three crest wells plus two 1Up lights. */
export const MAX_PIPS = 5;

export function litCount(hp: number): number {
  return Math.max(0, Math.min(MAX_PIPS, hp | 0));
}

export function pipOn(hp: number, index: number): boolean {
  return index >= 0 && index < litCount(hp);
}

/** 1 at the start of each flash, falling to 0, and 0 in the gaps. */
export function hitStrength(elapsedMs: number, flashes = HIT_FLASHES): number {
  const local = flashLocal(elapsedMs, flashes);
  if (local < 0) return 0;
  return 1 - local / HIT_PULSE_MS;
}

/** The lost life blinks in step with the flashes, then stays dark. */
export function drainStrength(elapsedMs: number, flashes = HIT_FLASHES): number {
  return hitStrength(elapsedMs, flashes);
}

/** Expanding ring on the front of each flash. */
export function impactStrength(elapsedMs: number, flashes = HIT_FLASHES): number {
  const local = flashLocal(elapsedMs, flashes);
  if (local < 0 || local >= IMPACT_MS) return 0;
  return 1 - local / IMPACT_MS;
}

export interface HitShake {
  /** Offset in cells. Positive x is to the right. */
  readonly x: number;
  readonly y: number;
  /** Radians, about the crest centre. */
  readonly rot: number;
}

/** Fast decaying rattle for the whole hit, including the gaps between flashes. */
export function hitShake(elapsedMs: number, flashes = HIT_FLASHES): HitShake {
  if (!hitActive(elapsedMs, flashes)) return { x: 0, y: 0, rot: 0 };
  const decay = 1 - elapsedMs / flashSpan(flashes);
  const swing = Math.sin(elapsedMs * 0.072) * decay;
  return { x: swing * 0.28, y: Math.sin(elapsedMs * 0.13) * decay * 0.07, rot: swing * 0.1 };
}

/** Body sprite fitted onto the two-cell base. Wings may overlap adjacent empty cells. */
export const TOTEM_FIT = { maxW: 2.28, maxH: 1.48 } as const;

export type TotemSocket = { readonly u: number; readonly v: number; readonly s: number };

/**
 * HP wells on the crest texture (0–1 UV). Index 0 is the last remaining life.
 * Measured from the keyed Image B orange body; turquoise shares the silhouette.
 */
export const TOTEM_SOCKETS: readonly TotemSocket[] = [
  { u: 0.397, v: 0.755, s: 0.078 },
  { u: 0.499, v: 0.756, s: 0.078 },
  { u: 0.6, v: 0.755, s: 0.078 },
];

/** Lives 4 and 5, won with 1Up bonuses: shown only while lit, either side of the wells. */
export const TOTEM_BONUS_SOCKETS: readonly TotemSocket[] = [
  { u: 0.701, v: 0.755, s: 0.07 },
  { u: 0.297, v: 0.755, s: 0.07 },
];

export const TOTEM_CRYSTAL = { u: 0.5, v: 0.34, su: 0.3, sv: 0.48 } as const;
